import type { LoginRo } from './api.generated';

/** Same origin rule as baseQuery, without importing the store. */
const getApiOrigin = (): string => {
  const configured = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_DEVELOPMENT_BASE_URL : process.env.REACT_APP_PRODUCTION_BASE_URL;
  if (!configured || configured === 'auto') return window.location.origin;
  return configured.replace(/\/$/, '');
};

/**
 * Public SSO calls of the login pages. Plain fetch: these run before there
 * is a session. The owner settings page uses ssoApi.ts (RTK Query).
 */
export type SsoStatus = { enabled: boolean; microsoft: boolean };
export type SsoDiscovery = { mode: 'sso'; connectionId: string; displayName: string } | { mode: 'password' };

/** Error codes the API puts in /login?ssoError=… (copy: sso.error.<code>). */
export const SSO_ERROR_CODES = [
  'disabled',
  'txInvalid',
  'idpError',
  'consentRequired',
  'cancelled',
  'tenantNotConfigured',
  'tokenInvalid',
  'emailUnverified',
  'domainNotAllowed',
  'accountConflict',
  'noAccount',
  'userInactive',
  'userLimitReached',
  'companyExpired',
  'mfaRequired',
  'idpUnreachable',
  'ssoRequired',
  'exchangeFailed',
  'generic',
] as const;
export type SsoErrorCode = (typeof SSO_ERROR_CODES)[number];

export const toSsoErrorCode = (value: unknown): SsoErrorCode | null => (typeof value === 'string' && (SSO_ERROR_CODES as readonly string[]).includes(value) ? (value as SsoErrorCode) : null);

const OFF: SsoStatus = { enabled: false, microsoft: false };
let statusPromise: Promise<SsoStatus> | null = null;

/** GET /auth/sso/status once per page load; any failure reads as off. */
export const fetchSsoStatus = (): Promise<SsoStatus> => {
  if (!statusPromise) {
    statusPromise = (async () => {
      try {
        if (typeof fetch !== 'function') return OFF;
        const response = await fetch(`${getApiOrigin()}/api/v1/auth/sso/status`);
        if (!response.ok) return OFF;
        const body = await response.json();
        return {
          enabled: body?.enabled === true,
          microsoft: body?.enabled === true && body?.microsoft === true,
        };
      } catch {
        return OFF;
      }
    })();
  }
  return statusPromise;
};

/** Tests only. */
export const resetSsoStatusCache = () => {
  statusPromise = null;
};

/** Only whether the email's domain uses SSO. Failure reads as password. */
export const discoverSso = async (email: string): Promise<SsoDiscovery> => {
  try {
    const response = await fetch(`${getApiOrigin()}/api/v1/auth/sso/discover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) return { mode: 'password' };
    const body = await response.json();
    if (body?.mode === 'sso' && typeof body.connectionId === 'string' && typeof body.displayName === 'string') {
      return {
        mode: 'sso',
        connectionId: body.connectionId,
        displayName: body.displayName,
      };
    }
  } catch {
    // fall through
  }
  return { mode: 'password' };
};

/** Full-page navigation target for "Sign in with Microsoft" / an org IdP. */
export const ssoStartUrl = (target: { provider: 'microsoft' } | { connectionId: string }, options: { returnTo?: string; loginHint?: string } = {}): string => {
  const params = new URLSearchParams();
  if ('provider' in target) params.set('provider', target.provider);
  else params.set('c', target.connectionId);
  if (options.returnTo && options.returnTo !== '/') {
    params.set('returnTo', options.returnTo);
  }
  if (options.loginHint) params.set('login_hint', options.loginHint);
  return `${getApiOrigin()}/api/v1/auth/sso/start?${params.toString()}`;
};

const exchanges = new Map<string, Promise<LoginRo>>();

/** One POST per code, also across a strict-mode remount. */
export const exchangeSsoHandoff = (code: string): Promise<LoginRo> => {
  const existing = exchanges.get(code);
  if (existing) return existing;
  const pending = (async () => {
    const response = await fetch(`${getApiOrigin()}/api/v1/auth/sso/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handoff: code }),
    });
    if (!response.ok) throw new Error('SSO exchange failed');
    const session = (await response.json()) as LoginRo;
    if (!session?.token || !session.user) {
      throw new Error('SSO exchange returned no session');
    }
    return session;
  })();
  exchanges.set(code, pending);
  return pending;
};
